import { isRecordFieldEqual, rowIdKey } from "@/utils/compareData";
import type { Deal } from "@/types/deal";

export type PipelineCellChange = { rowId: string; fields: string[] };

export type PipelineLiveMergeResult = {
  deals: Deal[];
  initialDeals: Deal[];
  /** Cells whose server value changed since the last snapshot (not local edits). */
  changedCells: PipelineCellChange[];
  /** Rows that appeared since the last snapshot. */
  addedRowIds: string[];
};

type Row = Record<string, unknown>;

const asRow = (deal: Deal) => deal as unknown as Row;

const valueOf = (deal: Deal | undefined, field: string): unknown =>
  deal ? asRow(deal)[field] : undefined;

function fieldsOf(...rows: Array<Deal | undefined>): Set<string> {
  const fields = new Set<string>();
  for (const row of rows) {
    if (!row) continue;
    for (const key of Object.keys(row)) fields.add(key);
  }
  return fields;
}

function locallyEditedFields(current: Deal, original: Deal | undefined) {
  const edited = new Set<string>();
  if (!original) return edited;
  for (const field of fieldsOf(current, original)) {
    if (
      !isRecordFieldEqual(valueOf(current, field), valueOf(original, field))
    ) {
      edited.add(field);
    }
  }
  return edited;
}

/**
 * Apply a fresh server snapshot of the pipeline to the working rows.
 *
 * - Fields the user has edited locally (working copy differs from the
 *   baseline) keep the local value, so a refresh never discards a draft.
 * - Every other field takes the server value, and the baseline becomes the
 *   server snapshot. Saving a draft then only sends the user's own edits on
 *   top of the latest values instead of overwriting newer changes.
 * - Rows that disappeared from the server (deleted, or moved out of the
 *   Active filter) are dropped unless they hold local edits.
 */
export function mergePipelineSnapshot(
  deals: Deal[],
  initialDeals: Deal[],
  fresh: Deal[],
): PipelineLiveMergeResult {
  const currentById = new Map<string, Deal>();
  for (const row of deals) {
    const key = rowIdKey(row.id);
    if (key) currentById.set(key, row);
  }
  const originalById = new Map<string, Deal>();
  for (const row of initialDeals) {
    const key = rowIdKey(row.id);
    if (key) originalById.set(key, row);
  }

  const mergedDeals: Deal[] = [];
  const changedCells: PipelineCellChange[] = [];
  const addedRowIds: string[] = [];
  const freshIds = new Set<string>();

  for (const freshRow of fresh) {
    const key = rowIdKey(freshRow.id);
    if (!key) continue;
    freshIds.add(key);

    const current = currentById.get(key);
    const original = originalById.get(key);

    if (!current) {
      mergedDeals.push(structuredClone(freshRow));
      if (deals.length > 0) addedRowIds.push(key);
      continue;
    }

    const edited = locallyEditedFields(current, original);
    const merged = asRow(structuredClone(freshRow));
    for (const field of edited) {
      merged[field] = valueOf(current, field);
    }
    mergedDeals.push(merged as unknown as Deal);

    const changedFields: string[] = [];
    for (const field of fieldsOf(freshRow, original)) {
      if (edited.has(field)) continue;
      if (
        !isRecordFieldEqual(valueOf(freshRow, field), valueOf(original, field))
      ) {
        changedFields.push(field);
      }
    }
    if (changedFields.length > 0) {
      changedCells.push({ rowId: key, fields: changedFields });
    }
  }

  for (const current of deals) {
    const key = rowIdKey(current.id);
    if (!key || freshIds.has(key)) continue;
    const original = originalById.get(key);
    if (locallyEditedFields(current, original).size > 0) {
      mergedDeals.push(current);
    }
  }

  return {
    deals: mergedDeals,
    initialDeals: structuredClone(fresh),
    changedCells,
    addedRowIds,
  };
}
